import {
  Color3,
  Color4,
  CreateScreenshotUsingRenderTargetAsync,
  DirectionalLight,
  HemisphericLight,
  Scene,
  type AbstractEngine,
  UniversalCamera,
  Vector3,
} from "@babylonjs/core";
import { HERO_LIST, type HeroId } from "@rivalwatch/shared";
import { animateAvatar, buildAvatar } from "./avatars.ts";
import { loadHeroModels } from "./models.ts";

/**
 * Encodes raw RGBA pixels as a PNG data URL with a 2D canvas. (Babylon's default dumper
 * lazy-loads a shader and spins up a helper engine, which can stall under Vite dev.)
 */
function dumpToPng(width: number, height: number, data: ArrayBufferView, done?: (d: string | ArrayBuffer) => void, _mime?: string, _file?: string, invertY?: boolean) {
  const src = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(width, height);
  const row = width * 4;
  for (let y = 0; y < height; y++) {
    const from = (invertY ? height - 1 - y : y) * row;
    img.data.set(src.subarray(from, from + row), y * row);
  }
  ctx.putImageData(img, 0, 0);
  done?.(canvas.toDataURL("image/png"));
}

/**
 * Renders a cel-shaded bust of every hero (or just `heroes`) with their in-game model and gear to
 * a transparent PNG, in a private offscreen scene with studio lighting. These stand in for Rivals'
 * painted portraits across the HUD, kill feed and menus (and star in the Brussels murals).
 */
export async function renderHeroPortraits(engine: AbstractEngine, size = 256, heroes: readonly HeroId[] = HERO_LIST.map((h) => h.id)): Promise<Partial<Record<HeroId, string>>> {
  const scene = new Scene(engine, { virtual: true });
  scene.useRightHandedSystem = true;
  scene.clearColor = new Color4(0, 0, 0, 0);
  scene.autoClear = true;
  scene.preventDefaultOnPointerDown = false;
  scene.preventDefaultOnPointerUp = false;
  scene.detachControl();

  const fill = new HemisphericLight("fill", new Vector3(0.3, 1, -0.4), scene);
  fill.intensity = 0.8;
  fill.groundColor = Color3.FromHexString("#3b3f5c");
  const key = new DirectionalLight("key", new Vector3(0.55, -0.6, 0.6), scene);
  key.intensity = 1.15;
  key.diffuse = Color3.FromHexString("#fff1d6");

  const cam = new UniversalCamera("portrait-cam", new Vector3(0, 0, -3), scene);
  cam.fov = 0.4;
  cam.minZ = 0.05;
  cam.maxZ = 40;
  cam.inputs.clear();
  scene.activeCamera = cam;

  const out: Partial<Record<HeroId, string>> = {};
  try {
    await loadHeroModels(scene);
    for (const h of HERO_LIST.filter((d) => heroes.includes(d.id))) {
      const av = buildAvatar(scene, h.id, null);
      try {
        // Three-quarter view, turned slightly toward the camera's left.
        av.root.rotation.y = -0.55;
        animateAvatar(av, {
          speed: 0,
          pitch: 0.12,
          crouch: false,
          grounded: true,
          dt: 1,
          action: 0,
          status: 0,
          v: [0, 0, 0, 0],
          form: 0,
          disguised: false,
          ultActive: false,
          moveKind: 0,
          self: false,
          friend: true,
          netId: 0,
          pos: { x: 0, y: 0, z: 0 },
          yaw: -0.55,
          eye: { x: 0, y: h.height - h.headRadius, z: 0 },
          aim: { x: 0, y: 0, z: -1 },
          lookup: () => undefined,
        });
        for (const m of av.meshes) m.alwaysSelectAsActiveMesh = true;
        const k = h.height / 1.8;
        const headY = h.height - h.headRadius * 1.1;
        // Stay outside the dressed body: some heroes' gear reaches well forward (snouts, mechs).
        const { min, max } = av.root.getHierarchyBoundingVectors(true, (m) => m.isVisible && m.isEnabled());
        const bodyHidden = !av.rig.parts.head?.isVisible && !av.rig.parts.torso?.isVisible;
        if (bodyHidden) {
          // Not humanoid (the model is hidden under gear): frame the front of whatever was built.
          const size = Math.max(max.x - min.x, max.y - min.y, 0.5);
          const target = new Vector3((min.x + max.x) / 2, min.y + (max.y - min.y) * 0.55, min.z + (max.z - min.z) * 0.3);
          cam.position.set(target.x + 0.3 * size, target.y + 0.2 * size, min.z - 1.1 * size);
          cam.setTarget(target);
        } else {
          cam.position.set(0.35 * k, headY + 0.1 * k, Math.min(-2.3 * k, min.z - 0.9));
          cam.setTarget(new Vector3(0, headY - 0.28 * k, 0));
        }
        await scene.whenReadyAsync();
        out[h.id] = await CreateScreenshotUsingRenderTargetAsync(
          engine,
          cam,
          { width: size, height: size },
          "image/png",
          4,
          true,
          undefined,
          false,
          false,
          false,
          undefined,
          (tex) => {
            tex.renderList = av.meshes.slice();
          },
          dumpToPng,
        );
      } finally {
        av.dispose();
      }
    }
  } finally {
    scene.dispose();
  }
  return out;
}
